import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "../../lib/cn.js";

type Variant = "primary" | "secondary" | "ghost" | "destructive" | "promo";
type Size = "sm" | "md" | "lg";

const BASE_CLASSES =
  "inline-flex items-center justify-center gap-2 rounded font-medium transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed";

const VARIANT_CLASSES: Record<Variant, string> = {
  primary: "bg-primary text-primary-fg hover:opacity-90 disabled:opacity-50",
  // Bordered for the same reason `promo` is: bg-muted sits at ~1.06:1 against
  // a card, so the fill alone gives these buttons no edge at all - they read
  // as tinted text, not as controls.
  //
  // It uses --color-control-border rather than --color-border, and that was
  // not the first attempt: `border-border` is 1.27:1 against a card, which is
  // a number I measured and then still described as a visible edge. It is
  // not. Put one beside the trial button's 3.91:1 and the grey one has no
  // outline at all. The control token clears 3:1 in both themes.
  //
  // The hover border is not decoration either: the hover FILL is `bg-border`,
  // so without moving the stroke the edge would flatten at exactly the moment
  // interaction feedback matters most.
  secondary:
    "border border-control-border bg-muted text-foreground hover:border-muted-fg hover:bg-border disabled:opacity-50",
  ghost: "bg-transparent text-foreground hover:bg-muted disabled:opacity-50",
  destructive: "bg-destructive text-destructive-fg hover:opacity-90 disabled:opacity-50",
  // Bordered, and load-bearing rather than decorative: the fill sits at
  // 1.22:1 against the card in light theme, so without the border the button
  // has no visible edge. `secondary` carries one for the same reason; this
  // one reaches 3.95:1 and clears the 3:1 floor for a UI-component boundary,
  // which `secondary`'s does not. See --color-trial in index.css.
  promo: "border border-trial-border bg-trial text-trial-fg hover:opacity-90 disabled:opacity-50",
};

const SIZE_CLASSES: Record<Size, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-6 text-base",
};

/**
 * Compose the Button primitive's classes without rendering a <button>.
 * Lets non-button elements (e.g. <Link> CTAs) share the exact same visual
 * styling as <Button> without going through Radix Slot.
 */
export function buttonVariants({
  variant = "primary",
  size = "md",
  className,
}: {
  variant?: Variant;
  size?: Size;
  className?: string;
} = {}): string {
  return cn(BASE_CLASSES, VARIANT_CLASSES[variant], SIZE_CLASSES[size], className);
}

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = "primary", size = "md", className, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={buttonVariants({ variant, size, className })}
      {...rest}
    />
  );
});
